/**
 * Ops Command Center — the "mission control" for platform admins.
 * Live visitor pulse (polled every 4s), today's revenue ticker, and
 * super-admin kill-switches (freeze payouts, disable signups).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Activity, Globe, ShieldAlert, Ban, Zap, Users as UsersIcon,
  CircleDollarSign, PiggyBank, TrendingUp, Radio, AlertTriangle,
  Trophy, ExternalLink, Wallet, X, CheckCircle2, XCircle, RotateCcw, Download,
} from "lucide-react";
import { AreaChart, Area, Tooltip, ResponsiveContainer } from "recharts";

const POLL_MS = 4000;

function KpiTile({ label, value, sub, icon: Icon, tone = "cyan", testid, spark }) {
  const tones = {
    cyan:  { bg: "from-cyan-500/15 to-cyan-500/0 border-cyan-500/20 text-cyan-100",   stroke: "#22d3ee", fill: "#22d3ee" },
    lav:   { bg: "from-indigo-500/15 to-indigo-500/0 border-indigo-500/20 text-indigo-100", stroke: "#a5b4fc", fill: "#a5b4fc" },
    mint:  { bg: "from-emerald-500/15 to-emerald-500/0 border-emerald-500/20 text-emerald-100", stroke: "#34d399", fill: "#34d399" },
    rose:  { bg: "from-rose-500/15 to-rose-500/0 border-rose-500/20 text-rose-100",   stroke: "#fb7185", fill: "#fb7185" },
    amber: { bg: "from-amber-500/15 to-amber-500/0 border-amber-500/20 text-amber-100", stroke: "#fbbf24", fill: "#fbbf24" },
  };
  const t = tones[tone];
  const gradId = `spark-${testid || tone}`;
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${t.bg} p-5 backdrop-blur-md relative overflow-hidden`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="text-3xl font-display font-medium tabular-nums" style={{ color: "inherit" }}>{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
      {spark && spark.length > 1 && (
        <div className="h-14 -mx-2 -mb-2 mt-2" data-testid={`${testid}-sparkline`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={t.fill} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={t.fill} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip
                cursor={{ stroke: t.stroke, strokeDasharray: "2 2", strokeOpacity: 0.5 }}
                contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 11, padding: "6px 10px", color: "#e2e8f0" }}
                labelStyle={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}
                formatter={(v) => [cad(v), "GMV"]}
                labelFormatter={(l) => l + " UTC"}
              />
              <Area type="monotone" dataKey="gmv" stroke={t.stroke} strokeWidth={1.75}
                    fill={`url(#${gradId})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 5) return "now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function fmtMoney(n) {
  return cad(n ?? 0);
}

function fmtPeriodLabel(period) {
  if (!period) return "—";
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, 1)).toLocaleDateString(undefined, {
    month: "short", year: "numeric", timeZone: "UTC",
  });
}

function PayoutOverrideModal({ reseller, isSuper, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);   // period being mutated
  const [voidPeriod, setVoidPeriod] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  async function load() {
    try {
      const { data } = await api.get(`/admin/resellers/${reseller.reseller_id}/payouts?months=12`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load ledger");
    }
  }
  useEffect(() => { load(); }, [reseller.reseller_id]);

  async function apply(period, status, reason) {
    setBusy(period);
    try {
      const body = { status };
      if (reason) body.reason = reason;
      await api.post(`/admin/resellers/${reseller.reseller_id}/payouts/${period}`, body);
      toast.success(status === "clear"
        ? `Cleared override on ${fmtPeriodLabel(period)}`
        : status === "paid"
          ? `Marked ${fmtPeriodLabel(period)} as paid`
          : `Voided ${fmtPeriodLabel(period)}`);
      setVoidPeriod(null); setVoidReason("");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Override failed"));
    } finally { setBusy(null); }
  }

  function downloadCsv() {
    if (!data?.items?.length) return;
    const headers = ["Period", "Conversions", "Voided", "GMV", "Performance Fees", "Commission Rate", "Commission", "Status", "Settled At", "Voided Reason", "Reviewer"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const row of data.items) {
      lines.push([
        row.period, row.conversions, row.voided, row.gmv, row.performance_fees,
        row.commission_rate, row.commission, row.status,
        row.paid_at || "", row.voided_reason || "", row.reviewer_email || "",
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const slug = (reseller.business_name || "reseller").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    a.download = `cyannect-payouts-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
  }

  const fmtMoney = (n) => cad(n || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" data-testid="ops-payout-modal">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div>
            <div className="inline-flex items-center gap-1.5 text-amber-400 text-[10px] uppercase tracking-widest font-semibold">
              <Wallet className="w-3.5 h-3.5" /> Payout override
            </div>
            <h3 className="text-lg font-display mt-1" style={{ color: "#f1f5f9" }}>{reseller.business_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isSuper ? "Flip any period to paid or voided. Reason is required for voids and audit-logged." : "Read-only for admins. Super-admin required to write overrides."}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-800 text-slate-400" data-testid="ops-payout-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-2 border-b border-slate-800/60 flex items-center justify-end">
          <button
            onClick={downloadCsv}
            disabled={!data?.items?.length}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 hover:bg-white text-slate-900 disabled:opacity-40"
            data-testid="ops-payout-download-csv"
            title="Download this reseller's ledger as CSV (for finance)"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {!data && <div className="text-sm text-slate-500 italic text-center py-8">Loading ledger…</div>}
          {data && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-2 py-2 text-left font-semibold">Period</th>
                  <th className="px-2 py-2 text-right font-semibold">Conv.</th>
                  <th className="px-2 py-2 text-right font-semibold">GMV</th>
                  <th className="px-2 py-2 text-right font-semibold">Fees</th>
                  <th className="px-2 py-2 text-right font-semibold">Commission</th>
                  <th className="px-2 py-2 text-left font-semibold">Status</th>
                  <th className="px-2 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {data.items.map((row) => {
                  const overridden = !row.is_synthetic;
                  const running = busy === row.period;
                  return (
                    <tr key={row.period} data-testid={`ops-payout-row-${row.period}`}
                        className={row.status === "empty" ? "text-slate-500" : "text-slate-200"}>
                      <td className="px-2 py-2 font-medium">{fmtPeriodLabel(row.period)}
                        {overridden && (
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase tracking-widest">override</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums">{row.conversions}
                        {row.voided > 0 && <span className="ml-1 text-[10px] text-rose-400">(−{row.voided})</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-400">{fmtMoney(row.gmv)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-400">{fmtMoney(row.performance_fees)}</td>
                      <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${
                        row.status === "paid" ? "text-emerald-300" :
                        row.status === "upcoming" ? "text-cyan-300" :
                        row.status === "voided" ? "text-rose-300" : "text-slate-500"
                      }`}>{fmtMoney(row.commission)}</td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          row.status === "paid" ? "bg-emerald-500/20 text-emerald-300" :
                          row.status === "upcoming" ? "bg-cyan-500/20 text-cyan-300" :
                          row.status === "voided" ? "bg-rose-500/20 text-rose-300" :
                          "bg-slate-700 text-slate-400"
                        }`}>{row.status}</span>
                        {row.voided_reason && <div className="text-[10px] text-rose-400/80 mt-1 truncate max-w-[180px]" title={row.voided_reason}>{row.voided_reason}</div>}
                      </td>
                      <td className="px-2 py-2 text-right space-x-1">
                        {isSuper ? (
                          <>
                            <button
                              disabled={running || row.status === "paid"}
                              onClick={() => apply(row.period, "paid")}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px] font-semibold disabled:opacity-30"
                              data-testid={`ops-payout-mark-paid-${row.period}`}
                              title="Mark this period as paid"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Paid
                            </button>
                            <button
                              disabled={running || row.status === "voided"}
                              onClick={() => { setVoidPeriod(row.period); setVoidReason(""); }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-[10px] font-semibold disabled:opacity-30"
                              data-testid={`ops-payout-void-${row.period}`}
                              title="Void this period"
                            >
                              <XCircle className="w-3 h-3" /> Void
                            </button>
                            {overridden && (
                              <button
                                disabled={running}
                                onClick={() => apply(row.period, "clear")}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-700 hover:bg-slate-600 text-slate-200 text-[10px] font-semibold disabled:opacity-30"
                                data-testid={`ops-payout-clear-${row.period}`}
                                title="Clear override (revert to auto)"
                              >
                                <RotateCcw className="w-3 h-3" /> Clear
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-[10px] text-slate-500">Super-admin only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Void reason prompt */}
        {voidPeriod && (
          <div className="border-t border-slate-800 bg-slate-900 p-4" data-testid="ops-payout-void-prompt">
            <div className="text-xs text-slate-400 mb-2">
              Voiding <strong className="text-rose-300">{fmtPeriodLabel(voidPeriod)}</strong> — reason will be audit-logged and shown to the reseller.
            </div>
            <div className="flex gap-2">
              <input
                autoFocus value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. chargeback wave, TOS violation, fraud review"
                className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-xs font-mono focus:outline-none focus:border-rose-400"
                data-testid="ops-payout-void-reason-input"
              />
              <button onClick={() => { setVoidPeriod(null); setVoidReason(""); }}
                      className="px-3 py-2 rounded-lg text-xs text-slate-300 hover:text-white">
                Cancel
              </button>
              <button
                disabled={!voidReason.trim() || busy === voidPeriod}
                onClick={() => apply(voidPeriod, "voided", voidReason.trim())}
                className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-white text-xs font-semibold disabled:opacity-40"
                data-testid="ops-payout-void-confirm"
              >
                {busy === voidPeriod ? "Voiding…" : "Confirm void"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResellerLeaderboard({ isSuper }) {
  const [range, setRange] = useState("30d");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  async function load(r) {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/resellers/leaderboard?range=${r}&limit=10`);
      setRows(data.items || []);
    } catch (e) {
      setRows([]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(range); }, [range]);

  return (
    <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-6 backdrop-blur-md" data-testid="ops-reseller-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5" /> Reseller leaderboard
          </div>
          <div className="text-lg font-display mt-0.5" style={{ color: "#f8fafc" }}>Top 10 by attributed GMV</div>
        </div>
        <div className="inline-flex items-center rounded-full bg-slate-800/70 p-1" data-testid="ops-reseller-range-toggle">
          {["7d", "30d", "90d"].map(r => (
            <button key={r} onClick={() => setRange(r)}
                    data-testid={`ops-reseller-range-${r}`}
                    className={`text-[10px] font-semibold px-2.5 py-1 rounded-full transition ${range === r ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-200"}`}>
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-xs text-slate-500 italic text-center py-8">Loading leaderboard…</div>}
      {!loading && rows && rows.length === 0 && (
        <div className="text-xs text-slate-500 italic text-center py-8" data-testid="ops-reseller-leaderboard-empty">
          No reseller-attributed conversions in this window.
        </div>
      )}
      {!loading && rows && rows.length > 0 && (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-2 py-1.5 text-left font-semibold">#</th>
                <th className="px-2 py-1.5 text-left font-semibold">Reseller</th>
                <th className="px-2 py-1.5 text-right font-semibold">Clicks</th>
                <th className="px-2 py-1.5 text-right font-semibold">Conv.</th>
                <th className="px-2 py-1.5 text-right font-semibold">CVR</th>
                <th className="px-2 py-1.5 text-right font-semibold">GMV</th>
                <th className="px-2 py-1.5 text-right font-semibold">Fees</th>
                <th className="px-2 py-1.5 text-right font-semibold">Commission</th>
                <th className="px-2 py-1.5 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {rows.map((r, i) => {
                const rankTone = i === 0 ? "text-amber-300" : i === 1 ? "text-slate-200" : i === 2 ? "text-orange-300" : "text-slate-500";
                return (
                  <tr key={r.reseller_id} className="hover:bg-slate-800/40 transition"
                      data-testid={`ops-reseller-row-${i}`}>
                    <td className={`px-2 py-2 font-mono font-semibold ${rankTone}`}>#{i + 1}</td>
                    <td className="px-2 py-2">
                      <div className="text-slate-100 font-medium truncate max-w-[220px]">{r.business_name}</div>
                      {r.website && (
                        <a href={r.website} target="_blank" rel="noreferrer"
                           className="text-[10px] text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1 font-mono truncate max-w-[220px]">
                          {r.website.replace(/^https?:\/\//, "")}
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-slate-300">{Number(r.clicks || 0).toLocaleString()}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-300">{Number(r.conversions || 0).toLocaleString()}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-400">{(r.conversion_rate || 0).toFixed(2)}%</td>
                    <td className="px-2 py-2 text-right font-mono text-cyan-300">{fmtMoney(r.gmv)}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-400">{fmtMoney(r.performance_fees)}</td>
                    <td className="px-2 py-2 text-right font-mono text-emerald-300 font-semibold" data-testid={`ops-reseller-commission-${i}`}>
                      {fmtMoney(r.commission_earned)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-semibold"
                        data-testid={`ops-reseller-manage-${i}`}
                        title="Manage payouts"
                      >
                        <Wallet className="w-3 h-3" /> Payouts
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <PayoutOverrideModal reseller={selected} isSuper={isSuper} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

export default function Ops() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [pulse, setPulse] = useState(null);
  const [rev, setRev] = useState(null);
  const [flags, setFlags] = useState(null);
  const [spark, setSpark] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {flag, next, prompt}
  const [reason, setReason] = useState("");
  const timerRef = useRef(null);

  async function pollAll() {
    try {
      const [p, r, f, sp] = await Promise.all([
        api.get("/admin/ops/pulse"),
        api.get("/admin/ops/revenue-ticker"),
        api.get("/admin/ops/flags"),
        api.get("/admin/ops/revenue-sparkline?hours=24"),
      ]);
      setPulse(p.data);
      setRev(r.data);
      setFlags(f.data);
      setSpark(sp.data);
    } catch (e) {
      // Silent — no alarm on transient errors, we retry every POLL_MS
    }
  }

  useEffect(() => {
    pollAll();
    timerRef.current = setInterval(pollAll, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  async function commitFlag() {
    if (!confirm) return;
    setBusy(true);
    try {
      const patch = { [confirm.flag]: confirm.next, reason: reason.trim() };
      const { data } = await api.post("/admin/ops/flags", patch);
      setFlags(data);
      toast.success(confirm.next ? `Kill-switch engaged: ${confirm.flag}` : `Kill-switch released: ${confirm.flag}`);
      setConfirm(null);
      setReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  }

  const deltaGmv = useMemo(() => {
    if (!rev) return null;
    const y = rev.yesterday?.gmv || 0;
    const t = rev.today?.gmv || 0;
    if (!y) return t > 0 ? "+∞" : "—";
    const pct = ((t - y) / y) * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}% vs yesterday`;
  }, [rev]);

  return (
    <div className="min-h-screen -m-4 sm:-m-6 p-6 lg:p-8 bg-slate-950 text-slate-100 relative" data-testid="ops-command-center">
      {/* Command-center aesthetic — dark, restrained, glowing accents */}
      <div className="absolute inset-0 pointer-events-none opacity-25"
           style={{
             backgroundImage:
               "radial-gradient(circle at 20% 10%, #06b6d4 0%, transparent 45%), radial-gradient(circle at 85% 90%, #a855f7 0%, transparent 40%)",
           }}
      />
      <div className="relative z-10 max-w-[1600px] mx-auto">
        <header className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-[11px] uppercase tracking-widest font-semibold mb-1">
              <Radio className="w-3.5 h-3.5 animate-pulse" /> Live · Command Center
            </div>
            <h1 className="font-display text-4xl tracking-tight" style={{ color: "#f8fafc" }}>Ops Console</h1>
            <p className="text-sm text-slate-400 mt-1">Global visitor pulse · Revenue ticker · Platform kill-switches</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Last refresh</div>
            <div className="text-xs font-mono text-slate-300">{pulse?.now ? timeAgo(pulse.now) : "…"}</div>
          </div>
        </header>

        {/* Flag banner — very visible when a kill-switch is active */}
        {flags && (flags.payouts_frozen || flags.signups_disabled) && (
          <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-100 flex items-start gap-3" data-testid="ops-flag-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-rose-300" />
            <div className="flex-1">
              <div className="font-semibold">
                Platform kill-switch engaged
                {flags.payouts_frozen && <span className="ml-2 px-2 py-0.5 rounded-full bg-rose-500/30 text-[10px] uppercase tracking-widest">payouts frozen</span>}
                {flags.signups_disabled && <span className="ml-2 px-2 py-0.5 rounded-full bg-rose-500/30 text-[10px] uppercase tracking-widest">signups off</span>}
              </div>
              <div className="text-xs text-rose-200/80 mt-1">
                Reason: {flags.reason || "—"} · by {flags.updated_by || "unknown"} · {timeAgo(flags.updated_at)}
              </div>
            </div>
          </div>
        )}

        {/* Revenue ticker */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiTile
            testid="ops-kpi-gmv"
            label="Today's GMV"
            value={fmtMoney(rev?.today?.gmv)}
            sub={`${rev?.today?.conversions ?? 0} conversions · ${deltaGmv || ""}`}
            icon={TrendingUp}
            tone="cyan"
            spark={spark?.buckets}
          />
          <KpiTile
            testid="ops-kpi-fees"
            label="Performance fees"
            value={fmtMoney(rev?.today?.performance_fees)}
            sub={`vs ${fmtMoney(rev?.yesterday?.performance_fees)} yesterday`}
            icon={CircleDollarSign}
            tone="mint"
          />
          <KpiTile
            testid="ops-kpi-cashback"
            label="Cashback issued"
            value={fmtMoney(rev?.today?.cashback_issued)}
            sub={`${rev?.today?.cashback_count ?? 0} credits today`}
            icon={PiggyBank}
            tone="lav"
          />
          <KpiTile
            testid="ops-kpi-signups"
            label="New sign-ups"
            value={rev?.today?.new_users ?? 0}
            sub={`${rev?.today?.new_merchants ?? 0} merchants · ${rev?.fleet?.active_paid_subscriptions ?? 0} paid stores`}
            icon={UsersIcon}
            tone="amber"
          />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Live pulse — larger card */}
          <div className="lg:col-span-2 rounded-2xl bg-slate-900/60 border border-slate-800 p-6 backdrop-blur-md" data-testid="ops-pulse-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" /> Live visitor pulse
                </div>
                <div className="text-2xl font-display mt-1" style={{ color: "#f8fafc" }}>
                  <span data-testid="ops-active-visitors">{pulse?.active_visitors ?? 0}</span>{" "}
                  <span className="text-sm text-slate-400 font-normal">active now</span>
                </div>
              </div>
              <div className="text-right text-xs text-slate-400 space-y-0.5">
                <div>{pulse?.clicks_last_5min ?? 0} clicks / 5m</div>
                <div>{pulse?.clicks_last_hour ?? 0} clicks / 1h</div>
                <div className="text-slate-500">{pulse?.clicks_last_24h ?? 0} clicks / 24h</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                  <Globe className="w-3 h-3" /> Top countries · last hour
                </div>
                <ul className="space-y-1">
                  {(pulse?.by_country || []).slice(0, 6).map((c) => (
                    <li key={c.country} className="flex justify-between text-xs">
                      <span className="text-slate-300">{c.country || "unknown"}</span>
                      <span className="text-cyan-400 font-mono">{c.count}</span>
                    </li>
                  ))}
                  {(pulse?.by_country || []).length === 0 && (
                    <li className="text-xs text-slate-600 italic">No traffic in the last hour</li>
                  )}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                  <Zap className="w-3 h-3" /> Traffic sources · last hour
                </div>
                <ul className="space-y-1">
                  {(pulse?.by_traffic_source || []).slice(0, 6).map((s) => (
                    <li key={s.source} className="flex justify-between text-xs">
                      <span className="text-slate-300 capitalize">{s.source}</span>
                      <span className="text-indigo-400 font-mono">{s.count}</span>
                    </li>
                  ))}
                  {(pulse?.by_traffic_source || []).length === 0 && (
                    <li className="text-xs text-slate-600 italic">—</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent events</div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-800/60 divide-y divide-slate-800/60" data-testid="ops-recent-events">
              {(pulse?.recent_events || []).map((e) => (
                <div key={e.id} className="px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-800/40">
                  <div className="min-w-0">
                    <div className="text-slate-200 truncate">
                      <span className="text-cyan-400 font-mono">{(e.traffic_source || "direct").slice(0, 8)}</span>
                      {" · "}
                      <span className="text-slate-100">{e.store_name || "—"}</span>
                      {e.campaign_name && <span className="text-slate-500"> · {e.campaign_name}</span>}
                    </div>
                    <div className="text-slate-500 text-[10px] mt-0.5">
                      {[e.country, e.browser, e.device_type].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-slate-500 text-[10px] shrink-0 ml-2">{timeAgo(e.created_at)}</div>
                </div>
              ))}
              {(pulse?.recent_events || []).length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-600 italic text-center">Waiting for events…</div>
              )}
            </div>
          </div>

          {/* Kill-switches column */}
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-6 backdrop-blur-md" data-testid="ops-flag-payouts">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-rose-400 font-semibold flex items-center gap-1.5">
                    <ShieldAlert className="w-3.5 h-3.5" /> Freeze payouts
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Blocks new withdrawal requests + super-admin settlements.
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                  flags?.payouts_frozen ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/15 text-emerald-300"
                }`} data-testid="ops-payouts-status">
                  {flags?.payouts_frozen ? "frozen" : "flowing"}
                </span>
              </div>
              <div className="text-xs text-slate-500 mb-4">
                {rev?.fleet?.payouts_pending_count ?? 0} pending · {fmtMoney(rev?.fleet?.payouts_pending_total)}
              </div>
              <button
                disabled={!isSuper || busy}
                className={`w-full py-2 rounded-lg text-xs font-semibold transition ${
                  flags?.payouts_frozen
                    ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                    : "bg-rose-500 hover:bg-rose-400 text-white"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                onClick={() =>
                  setConfirm({
                    flag: "payouts_frozen",
                    next: !flags?.payouts_frozen,
                    prompt: flags?.payouts_frozen
                      ? "Release the payouts freeze? Withdrawals will resume."
                      : "Freeze ALL payouts platform-wide? Shoppers cannot withdraw + admins cannot settle.",
                  })
                }
                data-testid="ops-payouts-toggle"
              >
                {flags?.payouts_frozen ? "Release freeze" : "Freeze payouts"}
              </button>
              {!isSuper && <div className="text-[10px] text-slate-500 mt-2">Super-admin only</div>}
            </div>

            <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-6 backdrop-blur-md" data-testid="ops-flag-signups">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold flex items-center gap-1.5">
                    <Ban className="w-3.5 h-3.5" /> Disable sign-ups
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Returns 503 on POST /auth/register. Existing users unaffected.
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                  flags?.signups_disabled ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/15 text-emerald-300"
                }`} data-testid="ops-signups-status">
                  {flags?.signups_disabled ? "off" : "open"}
                </span>
              </div>
              <div className="text-xs text-slate-500 mb-4">
                {rev?.today?.new_users ?? 0} sign-ups today · {rev?.today?.new_merchants ?? 0} merchants
              </div>
              <button
                disabled={!isSuper || busy}
                className={`w-full py-2 rounded-lg text-xs font-semibold transition ${
                  flags?.signups_disabled
                    ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                    : "bg-amber-500 hover:bg-amber-400 text-slate-900"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                onClick={() =>
                  setConfirm({
                    flag: "signups_disabled",
                    next: !flags?.signups_disabled,
                    prompt: flags?.signups_disabled
                      ? "Re-open sign-ups? New shoppers + merchants can register again."
                      : "Pause sign-ups platform-wide? Existing users are unaffected.",
                  })
                }
                data-testid="ops-signups-toggle"
              >
                {flags?.signups_disabled ? "Re-open sign-ups" : "Pause sign-ups"}
              </button>
              {!isSuper && <div className="text-[10px] text-slate-500 mt-2">Super-admin only</div>}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <ResellerLeaderboard />
        </div>
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" data-testid="ops-confirm-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 p-6 shadow-2xl">
            <div className="inline-flex items-center gap-2 text-rose-400 text-[10px] uppercase tracking-widest font-semibold mb-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Confirm platform action
            </div>
            <div className="text-slate-100 text-sm mb-4">{confirm.prompt}</div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Reason (audit-logged)</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. incident response, month-end reconciliation…"
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-xs font-mono focus:outline-none focus:border-cyan-400 mb-4"
              data-testid="ops-confirm-reason"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 rounded-lg text-xs text-slate-300 hover:text-white transition"
                onClick={() => { setConfirm(null); setReason(""); }}
                data-testid="ops-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={commitFlag}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-white text-xs font-semibold transition disabled:opacity-40"
                data-testid="ops-confirm-submit"
              >
                {busy ? "Applying…" : (confirm.next ? "Engage" : "Release")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
